import type { Client } from "../../../types";
import { Button } from "../../../components/ui/button";
import { formatDate } from "../../utils/date";
import { ChevronLeft, Edit2, Phone, Mail, Calendar, User, MapPin, Shield } from "lucide-react";
import { useLanguage } from "../../../contexts/LanguageContext";
import { clientDetailsLabels } from "../labels";

interface Props {
  client: Client | null;
  loading: boolean;
  onEdit: () => void;
}

export function ClientInfoCard({ client, loading, onEdit }: Props) {
  const { lang } = useLanguage();
  const t = clientDetailsLabels[lang].detailPage;

  if (loading || !client) {
    return (
      <div className="bg-white rounded-3xl p-8 border border-neutral-100 shadow-sm animate-pulse">
        <div className="flex flex-col items-center gap-4">
          <div className="w-24 h-24 rounded-full bg-neutral-100" />
          <div className="h-6 w-32 bg-neutral-100 rounded" />
          <div className="h-4 w-48 bg-neutral-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-3xl border border-neutral-100 shadow-sm overflow-hidden transition-all">
      {/* Top Header Bar */}
      <div className="px-6 py-3 border-b border-neutral-50 flex items-center justify-between bg-neutral-50/30">
        <div className="flex items-center gap-2">
          <div className="w-1.5 h-4 bg-primary rounded-full" />
          <span className="text-[10px] font-bold uppercase text-neutral-500 tracking-[0.2em]">
            {t.infoTitle}
          </span>
        </div>
        <button
          onClick={onEdit}
          className="p-2 hover:bg-white rounded-xl transition-all text-primary border border-transparent hover:border-neutral-100 shadow-sm hover:shadow-none"
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>

      <div className="flex flex-col lg:flex-row items-stretch">
        {/* Left: Quick Actions */}
        <div className="p-8 lg:w-1/4 border-b lg:border-b-0 lg:border-r border-neutral-50 flex flex-col items-center text-center justify-center bg-gradient-to-b from-white to-neutral-50/20">
          <div className="space-y-4 mb-6">
            <div>
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">{t.labels.email}</p>
              <p className="text-sm text-neutral-700 font-bold truncate max-w-[180px]">{client.email || '-'}</p>
            </div>
            <div>
              <p className="text-[10px] font-bold text-neutral-400 uppercase tracking-widest mb-1">{t.labels.phone}</p>
              <p className="text-sm text-neutral-700 font-bold">{client.phone || '-'}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 w-full">
            {client.phone && (
              <a
                href={`https://wa.me/${client.phone.replace(/\D/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 py-2 px-3 bg-green-50 text-green-700 rounded-xl text-[10px] font-bold uppercase tracking-tight hover:bg-green-100 transition-colors border border-green-100/50"
              >
                <Phone className="w-3 h-3" />
                WhatsApp
              </a>
            )}
            {client.email && (
              <a
                href={`mailto:${client.email}`}
                className="flex items-center justify-center gap-2 py-2 px-3 bg-blue-50 text-blue-700 rounded-xl text-[10px] font-bold uppercase tracking-tight hover:bg-blue-100 transition-colors border border-blue-100/50"
              >
                <Mail className="w-3 h-3" />
                Email
              </a>
            )}
          </div>
        </div>

        {/* Right: Detailed Info */}
        <div className="flex-1 p-8 grid grid-cols-1 md:grid-cols-3 gap-8 items-start">
          {/* Personal Info */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-neutral-400">
              <User className="w-4 h-4" />
              <span className="text-[10px] uppercase font-bold tracking-widest">{t.sections.personal}</span>
            </div>
            <div className="space-y-4">
              {client.birth_date && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-tighter opacity-70">{t.labels.birthDate}</span>
                  <span className="text-sm text-neutral-700 font-bold">{formatDate(client.birth_date)}</span>
                </div>
              )}
              {client.cpf && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-tighter opacity-70">{t.labels.cpf}</span>
                  <span className="text-sm text-neutral-700 font-bold">{client.cpf}</span>
                </div>
              )}
              {client.gender && (
                <div className="flex flex-col gap-0.5">
                  <span className="text-[10px] text-neutral-400 font-bold uppercase tracking-tighter opacity-70">{t.labels.gender}</span>
                  <span className="text-sm text-neutral-700 font-bold">{client.gender}</span>
                </div>
              )}
            </div>
          </div>

          {/* Address */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-neutral-400">
              <MapPin className="w-4 h-4" />
              <span className="text-[10px] uppercase font-bold tracking-widest">{t.sections.address}</span>
            </div>
            <div className="flex flex-col gap-3">
              <div className="text-sm text-neutral-700 leading-relaxed font-bold">
                {client.street}{client.address_number ? `, ${client.address_number}` : ""}
                <br />
                <span className="text-neutral-400 font-medium">
                  {client.neighborhood && `${client.neighborhood}, `}{client.city} - {client.state}
                  <br />
                  {client.zip}
                </span>
              </div>
            </div>
          </div>

          {/* Insurance */}
          <div className="space-y-6">
            <div className="flex items-center gap-2 text-neutral-400">
              <Shield className="w-4 h-4" />
              <span className="text-[10px] uppercase font-bold tracking-widest">{t.sections.insurance}</span>
            </div>
            {(client.insurance || client.plan) ? (
              <div className="space-y-3">
                {client.insurance && (
                  <div className="flex flex-col gap-1 p-3 bg-neutral-50 rounded-2xl border border-neutral-100/50">
                    <span className="text-[9px] text-neutral-400 font-bold uppercase tracking-widest">{t.labels.insurance}</span>
                    <span className="text-xs font-bold text-neutral-700">{client.insurance}</span>
                  </div>
                )}
                {client.plan && (
                  <div className="flex flex-col gap-1 p-3 bg-neutral-50 rounded-2xl border border-neutral-100/50">
                    <span className="text-[9px] text-neutral-400 font-bold uppercase tracking-widest">{t.labels.plan}</span>
                    <span className="text-xs font-bold text-neutral-700">{client.plan}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="py-2 px-4 bg-neutral-50 rounded-xl border border-dashed border-neutral-200 text-[10px] font-bold text-neutral-400 uppercase tracking-widest text-center">
                Sem convênio
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
